import { version } from '../../package.json';
import { ESCAPE_KEY } from '../constants/commonConstants';
import NavigateEvent = globalThis.NavigateEvent;

const MEDIUM_MAX_WIDTH = 1023;

const setNextSiblingDisplay = (element: Element, display: string) => {
  // eslint-disable-next-line no-param-reassign -- not assigning
  (element.nextElementSibling! as HTMLElement).style.display = display;
};

const dispatchBenefitsEvent = () => {
  window.dispatchEvent(
    new CustomEvent('loadBenefits', {
      detail: { value: true },
    })
  );
};

const addScrollToDrawer = () => {
  const drawer = document.querySelector('#profile-drawer-dropdown') as HTMLElement;
  if (drawer) {
    const drawerTopPosition = drawer.getBoundingClientRect()?.top;
    const viewportHeight = window.innerHeight;
    const totalHeight = drawerTopPosition + drawer.scrollHeight;
    if (totalHeight > viewportHeight) {
      drawer.style.maxHeight = `${viewportHeight - drawerTopPosition}px`;
      drawer.style.overflowY = 'auto';
      drawer.style.borderRadius = '0';
    } else {
      drawer.style.maxHeight = 'none';
      drawer.style.overflowY = 'visible';
      drawer.style.borderRadius = '';
    }
  }
};

// this appends destpage to login/logout buttons
export const setLoginLogoutURL = (
  elements: (HTMLAnchorElement | null)[], event?: NavigateEvent
) => {
  elements?.forEach((element: HTMLAnchorElement | null) => {
    if (element) {
      const currentURL = element?.href;
      const destPageUrl = event?.destination?.url || window.location.href;
      if (currentURL) {
        const url = new window.URL(currentURL);
        url.searchParams.set('DestPage', destPageUrl);
        // eslint-disable-next-line no-param-reassign -- needed for url
        element.href = url.toString();
      }
    }
  });
};

const closeAllAccordions = () => {
  document.querySelectorAll('.accordion-panel').forEach((accordion) => {
    const panel = accordion as HTMLElement;
    const content = panel.nextElementSibling as HTMLElement;
    panel.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    content.style.height = '0px';
  });
};

function benefitDrawer() {
  document.addEventListener('click', (e) => {
    const element = e.target as HTMLElement;
    const accordion = element.classList.contains('accordion-panel') ? element
      : element.closest('.accordion-panel') as HTMLElement;

    if (accordion) {
      const isCurrentlyExpanded = accordion.getAttribute('aria-expanded') === 'true';
      const accordionContent = accordion.nextElementSibling as HTMLElement;
      closeAllAccordions();
      if (!isCurrentlyExpanded) {
        accordion.setAttribute('aria-expanded', 'true');
        accordionContent.setAttribute('aria-hidden', 'false');
        accordionContent.style.height = `${accordionContent.scrollHeight}px`;
      }
    }
    addScrollToDrawer();
  });
}

function trapFocusInDrawer(triggerButton: HTMLElement) {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (triggerButton.getAttribute('aria-expanded') !== 'true' || e.key !== 'Tab') {
      return;
    }
    const focusableElements: HTMLElement[] = [
      ...triggerButton.parentElement!.parentElement!.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
    ];
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else if (document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  };

  document.addEventListener('keydown', handleKeyDown);
}

function mobileMenu() {
  const burgerMenuButton = document!.querySelector<HTMLElement>('#burgerMenuButton');
  if (burgerMenuButton) {
    burgerMenuButton.addEventListener('click', ({ currentTarget }) => {
      const element = currentTarget as HTMLElement;
      const isCurrentlyExpanded = element.getAttribute('aria-expanded') === 'true';
      element!.setAttribute('aria-expanded', String(!isCurrentlyExpanded));
      setNextSiblingDisplay(element, isCurrentlyExpanded ? 'none' : 'block');
      dispatchBenefitsEvent();
    });
    trapFocusInDrawer(burgerMenuButton);
  }

  const navVerticalMenu = document!.querySelectorAll('.mobile nav ul > li button');
  navVerticalMenu.forEach((menu) => {
    menu.addEventListener('click', ({ currentTarget }) => {
      const currentTargetElement = currentTarget as HTMLElement;
      const isCurrentlyExpanded = currentTargetElement.getAttribute('aria-expanded') === 'true';
      currentTargetElement.setAttribute('aria-expanded', String(!isCurrentlyExpanded));
      setNextSiblingDisplay(currentTargetElement, isCurrentlyExpanded ? 'none' : 'flex');
    });
  });
}

function clickOutsideMenuDrawer(desktopMenuButtons: NodeListOf<HTMLButtonElement>) {
  document.addEventListener('click', (e) => {
    // this is needed as NodeListOf doesn't have the method some()
    const listItems: HTMLLIElement[] = [];
    document.querySelectorAll<HTMLLIElement>('.desktop nav > div > ul > li')
      .forEach((item) => { listItems.push(item); });
    const profileDrawerButton = document.querySelector<HTMLElement>('#profile-drawer-button');
    const profileDrawerDropdown = document.querySelector<HTMLElement>('#profile-drawer-dropdown');
    listItems.pop(); // remove profile drawer li from the list
    if (![...listItems].some((item) => item.contains(e.target as Node))) {
      desktopMenuButtons.forEach((menuButton) => {
        if (menuButton.id === 'profile-drawer-button') {
          return;
        }
        menuButton.setAttribute('aria-haspopup', 'false');
        menuButton.setAttribute('aria-expanded', 'false');
        menuButton.setAttribute('aria-current', '');
        setNextSiblingDisplay(menuButton, 'none');
      });
    }

    if (document.body.clientWidth > MEDIUM_MAX_WIDTH
        && profileDrawerButton && profileDrawerDropdown
        && !profileDrawerButton.contains(e.target as Node)
        && !profileDrawerDropdown?.contains(e.target as Node)
    ) {
      profileDrawerButton.setAttribute('aria-haspopup', 'false');
      profileDrawerButton.setAttribute('aria-expanded', 'false');
      profileDrawerButton.setAttribute('aria-current', '');
      setNextSiblingDisplay(profileDrawerButton, 'none');
      closeAllAccordions();
    }
  });
}

export function setGlovalNavLoginLogoutVisibility() {
  const loggedInIndicator = document.querySelector('#logged-in');
  const gnavLogout = document.querySelector<HTMLElement>('#gnav_logout');
  const gnavLogin = document.querySelector<HTMLElement>('#gnav_login');

  if (loggedInIndicator) {
    if (gnavLogout) gnavLogout.style.display = 'inline-block';
    if (gnavLogin) gnavLogin.style.display = 'none';
  } else {
    if (gnavLogout) gnavLogout.style.display = 'none';
    if (gnavLogin) gnavLogin.style.display = 'inline-block';
  }
}

function desktopMenu(isPartialOnly: boolean = false) {
  const desktopMenuButtons = document
    .querySelectorAll<HTMLButtonElement>('.desktop nav li > button');
  const profileDrawerDropdown = document.querySelector<HTMLElement>('#profile-drawer-dropdown');
  const profileDrawerButton = document.querySelector<HTMLElement>('#profile-drawer-button');
  desktopMenuButtons.forEach((menu) => {
    menu.addEventListener('click', ({ target }) => {
      const element = target as HTMLElement;
      if (menu.id === 'profile-drawer-button') {
        if (profileDrawerButton!.getAttribute('aria-expanded') === 'true') {
          profileDrawerButton!.setAttribute('aria-expanded', 'false');
          profileDrawerButton!.setAttribute('aria-current', 'false');
          profileDrawerDropdown!.style.display = 'none';
          closeAllAccordions();
          addScrollToDrawer();
        } else {
          profileDrawerButton!.setAttribute('aria-expanded', 'true');
          profileDrawerButton!.setAttribute('aria-current', 'page');
          profileDrawerDropdown!.style.display = 'block';
        }
      } else if (element!.getAttribute('aria-expanded') === 'true') {
          element!.setAttribute('aria-expanded', 'false');
          element!.setAttribute('aria-current', 'false');
          setNextSiblingDisplay(element!, 'none');
      } else {
        desktopMenuButtons.forEach((e) => {
          e.setAttribute('aria-expanded', 'false');
          e.setAttribute('aria-current', 'false');
          setNextSiblingDisplay(e, 'none');
        });
        element!.setAttribute('aria-expanded', 'true');
        element!.setAttribute('aria-current', 'page');
        setNextSiblingDisplay(element!, 'block');
      }
    });
    if (menu.id === 'profile-drawer-button') {
      menu.addEventListener('mouseover', () => {
        dispatchBenefitsEvent();
      });
    }
  });

  clickOutsideMenuDrawer(desktopMenuButtons);

  document.addEventListener('keyup', (e) => {
    if (e.code === ESCAPE_KEY) {
      e.preventDefault();
      desktopMenuButtons.forEach((menuButton) => {
        menuButton.setAttribute('aria-haspopup', 'false');
        menuButton.setAttribute('aria-expanded', 'false');
        menuButton.setAttribute('aria-current', '');
        setNextSiblingDisplay(menuButton, 'none');
      });
      profileDrawerButton!.setAttribute('aria-expanded', 'false');
      profileDrawerButton!.setAttribute('aria-current', 'false');
      profileDrawerDropdown!.style.display = 'none';
      closeAllAccordions();
    }
  });

  if (isPartialOnly) {
    setGlovalNavLoginLogoutVisibility();
  }
}

export function init(isPartial: boolean = false) {
  console.log('running nav script version:', version);
  const travelHeaderHeight = 56;
  let travelHeaderWrapper = document.querySelector<HTMLElement>('#travel-header-wrapper');
  let heightAboveTravelHeader = travelHeaderWrapper?.getBoundingClientRect()?.top || 0;
  const resizeObserver = new ResizeObserver((entries) => {
    travelHeaderWrapper = document.querySelector<HTMLElement>('#travel-header-wrapper');
    heightAboveTravelHeader = travelHeaderWrapper?.getBoundingClientRect()?.top || 0;
    const documentBodyWidth = entries[0].borderBoxSize[0].inlineSize;
    const desktopWrapper = document.querySelector<HTMLDivElement>('div.desktop');
    const mobileWrapper = document.querySelector<HTMLDivElement>('div.mobile');
    const desktopDropdownMenu = document.querySelectorAll<HTMLElement>('.desktop nav li > div');
    if (!!mobileWrapper && !!desktopWrapper) {
      if (documentBodyWidth > MEDIUM_MAX_WIDTH) {
        desktopDropdownMenu.forEach((e) => {
          e.style.top = `${heightAboveTravelHeader + travelHeaderHeight}px`;
        });
      }
    }
  });
  const desktopLogin = document!.querySelector<HTMLAnchorElement>('#desktopLogin') ?? null;
  const loginMobile = document!.querySelector<HTMLAnchorElement>('#mobileLogin') ?? null;
  const logoutMobile = document!.querySelector<HTMLAnchorElement>('#mobileLogout') ?? null;
  setLoginLogoutURL([loginMobile, logoutMobile, desktopLogin]);
  window?.navigation?.addEventListener('navigate', (event) => {
    setLoginLogoutURL([loginMobile, logoutMobile, desktopLogin], event);
  });
  resizeObserver.observe(document.body, { box: 'border-box' });
  mobileMenu();
  desktopMenu(isPartial);
  benefitDrawer();
}
